import { IUserContext } from '@truckdown/systems';
import { IAccountData } from './IAccountData';
import { IAccountManager } from './IAccountManager';
import { IAccountMailerManager } from './AccountMailer/IAccountMailerManager';
import { IAccountContactManager } from './AccountMailer/IAccountContactManager';

import { IUserData } from './Users/IUserData';
import { IUserManager } from './Users/IUserManager';
import { IUserCreator } from './Users/IUserCreator';
import { IUserAuthorizedChecker } from './Users/IUserAuthorizedChecker';
import { IApiKeyService } from './Integrations/IApiKeyService';
import { IApiTokenService } from './Integrations/IApiTokenService';

//RE-EXPORTS

export type { AddressModel } from './AddressModel';

export { AccountStatus } from './AccountStatus';
export { AccountType } from './AccountType';
export { UserType } from './Users/UserType';
export { UserStatus } from './Users/UserStatus';
export { MeasureUnit } from './Users/MeasureUnit';
export { SearchRadiusPreference } from './Users/SearchRadiusPreference';

export type { IAccountData };
export type { AccountModel } from './AccountModel';
export type { AccountPropertiesModel } from './AccountPropertiesModel';
export type { BasicAccountInfoModel } from './BasicAccountInfoModel';
export type { AccountSummaryModel } from './AccountSummaryModel';
export type { AccountInfoModel } from './AccountInfoModel';
export type { ContactModel } from './ContactModel';
export type { AccountCommentModel } from './AccountCommentModel';
export type { PhonesModel } from './PhonesModel';
export type { ClaimModel } from './ClaimModel';
export type { IdClaimModel } from './IdClaimModel';
export type { IdSettingModel } from './IdSettingModel';
export type { AccountQueryOptions } from './AccountQueryOptions';
export type { DuplicateAccountsModel } from './DuplicateAccountsModel';
export type { UpgradeRequestModel } from './UpgradeRequestModel';

export type { IAccountManager };
export type { NewAddressModel } from './NewAddressModel';
export type { CreateAccountModel } from './CreateAccountModel';
export type { AssignCSRModel } from './AssignCSRModel';
export type { UpdateAddressModel } from './UpdateAddressModel';
export type { UpdateCommentModel } from './UpdateCommentModel';
export type { DeleteCommentModel } from './DeleteCommentModel';
export type { UpdateAccountSystemInfo } from './UpdateAccountSystemInfo';
export type { UpdateAccountInfoModel } from './UpdateAccountInfoModel';
export type { AccountSubscriptionModel } from './AccountSubscriptionModel';
export type { UpdateAccountSubscriptionModel } from './UpdateAccountSubscriptionModel';
export type { UpdateAccountSubscriptionsModel } from './UpdateAccountSubscriptionsModel';
export type { UpdateAccountFleetInfoModel } from './UpdateAccountFleetInfoModel';

export type { IAccountMailerManager };
export type { AccountMailerModel } from './AccountMailer/AccountMailerModel'
export type { AccountMailerOptionsModel } from './AccountMailer/AccountMailerOptionsModel'
export type { MarketingListModel } from './AccountMailer/MarketingListModel'

export type { IAccountContactManager };
export type { AccountContactModel } from './AccountMailer/AccountContactModel';

export type { IUserData };
export type { UserModel } from './Users/UserModel';
export type { BasicUserInfoModel } from './Users/BasicUserInfoModel';
export type { CsrViewModel } from './Users/CsrViewModel';
export type { UserPropertiesModel } from './Users/UserPropertiesModel';

export type { IUserManager };
export type { CsrSettings } from './Users/CsrSettings';

export type { IUserAuthorizedChecker };
export type { HasAdminAccessToUserResponse } from './Users/HasAdminAccessToUserResponse';

export type { IUserCreator };
export { UserRegistrationType } from './Users/UserRegistrationType';
export { UserRightType } from './Users/UserRightType';
export type { InviteUserModel } from './Users/InviteUserModel';
export type { RegisterUserModel } from './Users/RegisterUserModel';
export type { UserInviteModel } from './Users/UserInviteModel';
export type { UpdateUserInfoModel } from "./Users/UpdateUserInfoModel";
export type { UpdateUserPreferencesModel } from "./Users/UpdateUserPreferencesModel";
export type { UpdateAdminUserSettingsModel } from "./Users/UpdateAdminUserSettingsModel";
export type { UpdateUserSecuritySettingsModel } from "./Users/UpdateUserSecuritySettingsModel";
export type { ChangePasswordModel } from "./Users/ChangePasswordModel";
export type { ChangeEmailModel } from "./Users/ChangeEmailModel";
export type { ChangeMobileModel } from "./Users/ChangeMobileModel";
export type { ChangeStatusModel } from "./Users/ChangeStatusModel";
export type { AddUserAdminModel } from './Users/AddUserAdminModel';
export type { AddUserModel } from "./Users/AddUserModel";
export type { MoveUserAdminModel } from "./Users/MoveUserAdminModel";

export type { IApiKeyService };
export type { ApiKeyModel } from './Integrations/ApiKeyModel';
export type { AddApiKeyModel } from './Integrations/AddApiKeyModel';
export type { EditApiKeyModel } from './Integrations/EditApiKeyModel';
export { ApiKeyStatus } from './Integrations/ApiKeyStatus';
export type { IApiTokenService };

export default function (context: IUserContext): void {
    context.registerService({
        name: 'IAccountData',
        activator: async () => {
            var svc = await import(/* webpackChunkName: "api-accounts-account-data" */ './AccountData');
            return new svc.AccountData(context) as IAccountData;
        }
    });

    context.registerService({
        name: 'IAccountManager',
        activator: async () => {
            var svc = await import(/* webpackChunkName: "api-accounts-account-manager" */ './AccountManager');
            return new svc.AccountManager(context) as IAccountManager;
        }
    });

    context.registerService({
        name: 'IAccountMailerManager',
        activator: async () => {
            var svc = await import(/* webpackChunkName: "api-accounts-mailer-manager" */ './AccountMailer/AccountMailerManager');
            return new svc.AccountMailerManager(context) as IAccountMailerManager;
        }
    });

    context.registerService({
        name: 'IAccountContactManager',
        activator: async () => {
            var svc = await import(/* webpackChunkName: "api-accounts-contact-manager" */ './AccountMailer/AccountContactManager');
            return new svc.AccountContactManager(context) as IAccountContactManager;
        }
    });

    context.registerService({
        name: 'IUserData',
        activator: async () => {
            var svc = await import(/* webpackChunkName: "api-accounts-user-data" */ './Users/UserData');
            return new svc.UserData(context) as IUserData;
        }
    });

    context.registerService({
        name: 'IUserManager',
        activator: async () => {
            var svc = await import(/* webpackChunkName: "api-accounts-user-manager" */ './Users/UserManager');
            return new svc.UserManager(context) as IUserManager;
        }
    });

    context.registerService({
        name: 'IUserAuthorizedChecker',
        activator: async () => {
            var svc = await import(/* webpackChunkName: "api-accounts-user-checker" */ './Users/UserAuthorizedChecker');
            return new svc.UserAuthorizedChecker(context) as IUserAuthorizedChecker;
        }
    });

    context.registerService({
        name: 'IUserCreator',
        activator: async () => {
            var svc = await import(/* webpackChunkName: "api-accounts-user-creator" */ './Users/UserCreator');
            return new svc.UserCreator(context) as IUserCreator;
        }
    });

    context.registerService({
        name: 'IApiKeyService',
        activator: async () => {
            var svc = await import(/* webpackChunkName: "api-accounts-apikey-service" */ './Integrations/ApiKeyService');
            return new svc.ApiKeyService(context) as IApiKeyService;
        }
    });

    context.registerService({
        name: 'IApiTokenService',
        activator: async () => {
            var svc = await import(/* webpackChunkName: "api-accounts-apitoken-service" */ './Integrations/ApiTokenService');
            return new svc.ApiTokenService(context) as IApiTokenService;
        }
    })
}
